"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { useQuery } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useAuth } from "./AuthContext";

// White-label branding for dispatch. Branding is resolved from EITHER:
//  1. the request subdomain (e.g. `acme` in acme.dispatch.vert.london) via the
//     public getWhitelabelTenant query — works pre-auth, so the login page is
//     branded; the subdomain is the same one the booking portal uses, and
//  2. otherwise the logged-in operator (getCompanyWhitelabel) on the shared URL.
// Either way we override the brand-accent CSS var (--color-brand-green, behind
// every *-brand-green class) with the operator colour. The status green
// (#4ab341) is a different token and stays unbranded.

const GET_COMPANY_WHITELABEL = gql`
  query GetCompanyWhitelabel($company_id: ID!) {
    getCompanyWhitelabel(company_id: $company_id) {
      company_id
      brand_name
      brand_color
      text_color
      logo_url
    }
  }
`;

const GET_WHITELABEL_TENANT = gql`
  query GetWhitelabelTenant($subdomain: String!) {
    getWhitelabelTenant(subdomain: $subdomain) {
      subdomain
      name
      logo_url
      primary_color
      text_color
      dispatch_enabled
      provider_company_id
    }
  }
`;

// Custom-domain resolution: when dispatch is reached on an operator's own
// domain (e.g. dispatcher.acme.com) rather than a *.dispatch.<root> subdomain.
const GET_WHITELABEL_TENANT_BY_HOST = gql`
  query GetWhitelabelTenantByHost($host: String!) {
    getWhitelabelTenantByHost(host: $host) {
      subdomain
      name
      logo_url
      primary_color
      text_color
      dispatch_enabled
      provider_company_id
    }
  }
`;

const ROOT_HOSTS = (
  process.env.NEXT_PUBLIC_DISPATCH_ROOT_HOST ||
  "dispatch.dev.vert.london,dispatch.vert.london,localhost:9191"
)
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

function subdomainFromHost(host) {
  if (!host) return null;
  const h = host.toLowerCase().split(":")[0];
  for (const root of ROOT_HOSTS) {
    const rootHost = root.split(":")[0];
    if (h === rootHost) return null; // bare root — no tenant
    if (h.endsWith("." + rootHost)) {
      const sub = h.slice(0, -("." + rootHost).length);
      if (!sub || sub === "www") return null;
      return sub;
    }
  }
  return null;
}

const BrandContext = createContext({
  brandName: null,
  brandLogo: null,
  brandColor: null,
  brandTextColor: null,
});

export const useBrand = () => useContext(BrandContext);

// Renders the operator's white-label logo when configured (a plain <img>, since
// brand logos are remote SVGs that next/image blocks by default), otherwise the
// bundled Vert logo via next/image. Drop-in replacement for the hardcoded logo
// <Image> elements — the parent keeps its relative sizing box.
export function BrandLogo({ fallback = "/assets/logo.png", className = "object-contain", priority = false }) {
  const { brandLogo } = useBrand();
  if (brandLogo) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={brandLogo} alt="logo" className={"w-full h-full " + className} />;
  }
  return <Image src={fallback} alt="logo" fill className={className} priority={priority} />;
}

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function normalizeHex(hex) {
  const h = hex.replace("#", "");
  return h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
}

function autoTextColor(hex) {
  const f = normalizeHex(hex);
  const r = parseInt(f.slice(0, 2), 16);
  const g = parseInt(f.slice(2, 4), 16);
  const b = parseInt(f.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#0a0a0a" : "#ffffff";
}

function darken(hex, factor) {
  const f = normalizeHex(hex);
  const ch = [0, 2, 4].map((i) =>
    Math.max(0, Math.round(parseInt(f.slice(i, i + 2), 16) * factor))
      .toString(16)
      .padStart(2, "0")
  );
  return "#" + ch.join("");
}

export function BrandProvider({ children }) {
  const { user, hasModule } = useAuth();
  const role = user?.user?.role;
  const companyId = user?.user?.company_id || user?.user?.id;

  // Resolve the subdomain client-side (host is only known in the browser).
  const [subdomain, setSubdomain] = useState(null);
  const [customHost, setCustomHost] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.host;
    const sub = subdomainFromHost(host);
    setSubdomain(sub);
    if (!sub) {
      const bare = host.toLowerCase().split(":")[0];
      const isRoot = ROOT_HOSTS.some((r) => r.split(":")[0] === bare);
      setCustomHost(isRoot ? null : host);
    }
  }, []);

  // Subdomain branding (public, pre-auth) takes precedence; otherwise fall back
  // to the logged-in company's branding on the shared URL.
  const { data: tenantData } = useQuery(GET_WHITELABEL_TENANT, {
    variables: { subdomain },
    skip: !subdomain,
  });
  const { data: hostData } = useQuery(GET_WHITELABEL_TENANT_BY_HOST, {
    variables: { host: customHost },
    skip: !customHost || !!subdomain,
  });
  const companyEnabled =
    !subdomain &&
    !customHost &&
    !!companyId &&
    role !== "super_admin" &&
    typeof hasModule === "function" &&
    hasModule("white label");
  const { data: companyData } = useQuery(GET_COMPANY_WHITELABEL, {
    variables: { company_id: companyId },
    skip: !companyEnabled,
  });

  // Subdomain branding for dispatch only applies when the operator's plan grants
  // the dispatch module (each portal is enabled individually).
  const tenantRaw = subdomain
    ? tenantData?.getWhitelabelTenant
    : customHost
    ? hostData?.getWhitelabelTenantByHost
    : null;
  const tenant = tenantRaw && tenantRaw.dispatch_enabled ? tenantRaw : null;
  const company = companyEnabled ? companyData?.getCompanyWhitelabel : null;

  const rawColor = tenant?.primary_color || company?.brand_color || null;
  const brandColor = rawColor && HEX.test(rawColor) ? rawColor : null;
  const rawText = tenant?.text_color || company?.text_color || null;
  const brandTextColor = rawText && HEX.test(rawText) ? rawText : null;
  const brandName = ((tenant?.name || company?.brand_name || "") + "").trim() || null;
  const brandLogo = tenant?.logo_url || company?.logo_url || null;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const STYLE_ID = "wl-brand-dispatch";
    const clear = () => {
      root.style.removeProperty("--color-brand-green");
      root.style.removeProperty("--color-brand-green-hover");
      root.style.removeProperty("--wl-brand-fg");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--wl-focus");
      root.removeAttribute("data-wl-dispatch");
      const el = document.getElementById(STYLE_ID);
      if (el) el.remove();
    };

    if (!brandColor) {
      clear();
      return;
    }

    const fg = brandTextColor || autoTextColor(brandColor);
    root.style.setProperty("--color-brand-green", brandColor);
    root.style.setProperty("--color-brand-green-hover", darken(brandColor, 0.85));
    root.style.setProperty("--wl-brand-fg", fg);
    // Field focus: brand the shadcn --ring token (inputs/selects focus border+ring)
    // and expose --wl-focus for JS-styled fields (react-select) to read.
    root.style.setProperty("--ring", brandColor);
    root.style.setProperty("--wl-focus", brandColor);
    root.setAttribute("data-wl-dispatch", "1");

    let st = document.getElementById(STYLE_ID);
    if (!st) {
      st = document.createElement("style");
      st.id = STYLE_ID;
      document.head.appendChild(st);
    }
    st.textContent = [
      ":root[data-wl-dispatch] .bg-brand-green{color:var(--wl-brand-fg)!important}",
      ":root[data-wl-dispatch] .bg-\\[\\#078b45\\]{background-color:var(--color-brand-green)!important;color:var(--wl-brand-fg)!important}",
      ":root[data-wl-dispatch] .border-\\[\\#078b45\\]{border-color:var(--color-brand-green)!important}",
      ":root[data-wl-dispatch] .ring-\\[\\#078b45\\]{--tw-ring-color:var(--color-brand-green)!important}",
      ":root[data-wl-dispatch] .text-\\[\\#078b45\\]{color:var(--color-brand-green)!important}",
      ":root[data-wl-dispatch] .hover\\:bg-\\[\\#078b45\\]\\/90:hover{background-color:var(--color-brand-green)!important;filter:brightness(0.92)}",
      // Field focus states that hardcode the status green (#4ab341) → brand.
      ":root[data-wl-dispatch] .focus\\:border-\\[\\#4ab341\\]:focus{border-color:var(--color-brand-green)!important}",
      ":root[data-wl-dispatch] .focus\\:ring-\\[\\#4ab341\\]:focus{--tw-ring-color:var(--color-brand-green)!important}",
      ":root[data-wl-dispatch] .focus\\:ring-\\[\\#4ab341\\]\\/10:focus{--tw-ring-color:color-mix(in srgb,var(--color-brand-green) 10%,transparent)!important}",
      ":root[data-wl-dispatch] .focus-visible\\:ring-\\[\\#4ab341\\]:focus-visible{--tw-ring-color:var(--color-brand-green)!important}",
    ].join("\n");

    return clear;
  }, [brandColor, brandTextColor]);

  // Page title → include the operator's brand name (e.g. "Dispatch - Acme").
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = brandName ? `Dispatch - ${brandName}` : "Dispatch Vert";
  }, [brandName]);

  // Favicon → the operator's logo (like the booking app). Injects a
  // <link rel="icon"> pointing at the brand logo, overriding the default
  // /favicon.ico; removed when no brand resolves.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const ID = "wl-favicon";
    const remove = () => {
      const el = document.getElementById(ID);
      if (el) el.remove();
    };
    if (!brandLogo) {
      remove();
      return;
    }
    let link = document.getElementById(ID);
    if (!link) {
      link = document.createElement("link");
      link.id = ID;
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
    }
    const isSvg = /\.svg(\?|$)/i.test(brandLogo);
    link.setAttribute("type", isSvg ? "image/svg+xml" : "image/png");
    link.setAttribute("href", brandLogo);
    return remove;
  }, [brandLogo]);

  return (
    <BrandContext.Provider value={{ brandName, brandLogo, brandColor, brandTextColor }}>
      {children}
    </BrandContext.Provider>
  );
}
